import React from 'react';
import {
  Alert,
  Box,
  Select, styled, TextField,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { IsearchAddressResult } from '@optimus/models';
import { PaymentMethodBankAccountType } from './enums';
import theme from '../assets/theme';

export type ErrorProps = {
  errorMessage?: string;
  optionalStyles?: React.CSSProperties;
};

type EditLinkProps = {
  updateEditState: (event: boolean) => void;
};

export interface IBankAccountFormInputs {
  bankAccountInfo: {
    businessName: string,
    accountType: PaymentMethodBankAccountType,
    routingNumber: string,
    accountNumber: string,
    businessAddress: IsearchAddressResult;
  }
}

export interface IPaperCheckFormInputs {
  paperCheckInfo: {
    businessName: string;
    businessAddress: IsearchAddressResult;
  }
}

export interface IFormInputs {
  principalInCharge: {
    isMemberPrincipalInCharge: string,
    name: string,
    title: string,
    email: string,
  },
  memberInfo: {
    firstName: string,
    lastName: string,
    email?: string,
    phone?: string,
  },
  memberConfig: {
    preferredLanguage: string,
  },
  companyConfig: {
    interestInInventorySolution: boolean,
    interestInAdditionalSolutions: boolean,
    partnerEnrollmentQuestions?: {
      [k: string]: unknown;
    } | null;
  },
  businessInfo: {
    name: string,
    email: string,
    phone: string,
    website?: string,
    role?: string,
    sameBusinessAndBillingAddress: boolean,
    address?: {
      street: string,
      city: string,
      state: string,
      country: string,
      zipCode: string,
    },
    autoAddress: IsearchAddressResult;
    billingAutoAddress: IsearchAddressResult;
    billingAddress?: {
      street: string,
      city: string,
      state: string,
      country: string,
      zipCode: string,
    },
    businessType: string,
  }
}

export const StyledTextField = styled(TextField)(() => ({
  width: '100%',
  paddingTop: theme.spacing(0.5),
  marginBottom: theme.spacing(2),
  '& fieldset': {
    borderRadius: theme.spacing(1.5),
    border: `${theme.spacing(0.125)} solid ${theme.palette.grey[200]}`,
  },
  '& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.grey[300],
    borderWidth: '2px',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.grey[300],
    borderWidth: '2px',
  },
  // Focused state - highlight with thicker border
  '& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.grey[300],
    borderWidth: '2px',
  },
  // Error state styling - always show red border when invalid
  '& .MuiInputBase-root.Mui-error .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.error.main,
    borderWidth: '2px',
  },
  '& .MuiInputBase-root.Mui-error:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.error.main,
    borderWidth: '2px',
  },
  '& .MuiInputBase-root.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.error.main,
    borderWidth: '2px',
  },
})) as unknown as typeof TextField;

export const StyledSelect = styled(Select)(() => ({
  width: '100%',
  marginTop: theme.spacing(0.5),
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.grey[200],
    borderRadius: theme.spacing(1.5),
    borderWidth: '1px',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.grey[300],
    borderWidth: '2px',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.grey[300],
    borderWidth: '2px',
  },
  '&.Mui-focused': {
    borderColor: theme.palette.grey[300],
  },
  // Error state styling - always show red border when invalid
  '&.Mui-error .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.error.main,
    borderWidth: '2px',
  },
  '&.Mui-error:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.error.main,
    borderWidth: '2px',
  },
  '&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.error.main,
    borderWidth: '2px',
  },
})) as unknown as typeof Select;

export const menuPaperProps = {
  borderRadius: theme.spacing(0.5),
  backgroundColor: theme.palette.common.white,
  boxShadow: '0 3px 14px 2px rgba(0, 0, 0, 0.12), 0 8px 10px 1px rgba(0, 0, 0, 0.14), 0 5px 5px -3px rgba(0, 0, 0, 0.20)',
};

export const styledSelectMenuProps = {
  PaperProps: {
    sx: menuPaperProps,
  },
};

export const ErrorAlert = ({ errorMessage, optionalStyles }: ErrorProps) => (<Alert
  icon={false}
  sx={() => ({
    fontSize: theme.spacing(1.75),
    background: theme.palette.common.white,
    color: theme.palette.error.main,
    p: 0,
    '& .MuiAlert-message': {
      padding: `${theme.spacing(0)} ! important`,
    },
    ...optionalStyles,
  })}
  severity="error"
>
  {errorMessage || 'This field is required'}
</Alert>);

export const EditOption = ({ updateEditState }: EditLinkProps) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      cursor: 'pointer',
    }}
    onClick={() => updateEditState(true)}
  >
    <Typography variant='h4'
      sx={() => ({
        color: theme.palette.brand.primary,
        mr: theme.spacing(0.5),
      })}
    >Edit
    </Typography>
    <EditIcon fontSize='inherit'
      sx={() => ({
        color: theme.palette.brand.primary,
      })}
    />
  </Box>
);
